import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useToast } from '@/shared/composables/useToast';
import { centralApiBaseUrl } from './tenantResolver';
import { toApiError } from './error';
import { createLocalUuid } from '@/shared/utils/uuid';

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/** Axios instance for the central super-admin API. Always hits the bare
 *  API host at `/api/*` (the backend routes are disambiguated at the
 *  guard level, not by URL prefix). Carries its own bearer token from
 *  useCentralAuthStore, kept separate from tenant auth. */
export const centralHttp: AxiosInstance = axios.create({ timeout: 15_000 });

centralHttp.interceptors.request.use(async (cfg) => {
  // Late import to avoid a circular init when the store imports the client.
  const { useCentralAuthStore } = await import('@/stores/central-auth.store');
  const auth = useCentralAuthStore();
  cfg.baseURL = centralApiBaseUrl();
  cfg.headers.set('Accept', 'application/json');
  if (auth.token) cfg.headers.set('Authorization', `Bearer ${auth.token}`);
  if (!cfg.headers.get('X-Request-ID')) {
    cfg.headers.set('X-Request-ID', createLocalUuid());
  }
  return cfg;
});

centralHttp.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const status = err.response?.status;
    const cfg = err.config as RetryConfig | undefined;

    if (status === 401) {
      const { useCentralAuthStore } = await import('@/stores/central-auth.store');
      await useCentralAuthStore().signOut('session-expired');
    }

    if (status === 403) {
      useToast().danger("You don't have permission for this action.");
    }

    if (status && status >= 500 && status <= 599 && cfg && !cfg._retry) {
      cfg._retry = true;
      await new Promise((resolve) => setTimeout(resolve, 500));
      try {
        return await centralHttp.request(cfg);
      } catch {
        /* fall through */
      }
    }

    return Promise.reject(toApiError(err));
  },
);
